import {difference, keys} from 'lodash';

import {
  buildCancelTokenSource,
  cancelledCancelToken,
  CancelToken,
  CancelTokenSource,
} from '../helpers/asyncCancelHelpers';
import {AppStore} from '../store/appStore';
import {makePanelCancelTokenId, PanelCancelTokenId} from '../store/panels/panelCancelTokenId';
import {getPanels} from '../store/panels/panelsSelectors';
import {NewPanelId} from '../store/panels/panelStateBase';

/*
 * State.
 */

const cancelTokenSources = new Map<PanelCancelTokenId, CancelTokenSource>();

/*
 * Subscription.
 */

export function setupPanelsCancellersSubscription(store: AppStore) {
  let lastPanelIds: ReadonlyArray<string> = [];

  store.subscribe(() => {
    // Find the panel IDs that were removed, if any.
    const panelIds = keys(getPanels(store.getState()));
    const removedPanelIds = difference(lastPanelIds, panelIds);
    lastPanelIds = panelIds;

    // For each removed panel ID, trigger and remove the corresponding cancellers.
    removedPanelIds.forEach((panelId) => {
      const idsToRemove = [...cancelTokenSources.keys()].filter((k) => k.startsWith(`${panelId}-`));
      idsToRemove.forEach(triggerPanelCancelToken);
    });
  });
}

/*
 * Api.
 */

export function buildPanelCancelToken(
  panelId: NewPanelId,
  previousCancelTokenId?: PanelCancelTokenId,
): [PanelCancelTokenId, CancelToken] {
  // If a previous ID was provided, try to cancel it.
  if (previousCancelTokenId) {
    triggerPanelCancelToken(previousCancelTokenId);
  }

  // Build the new CancelTokenSource and corresponding ID.
  const cancelTokenId = makePanelCancelTokenId(panelId);
  const cancelTokenSource = buildCancelTokenSource();

  // Keep track of the both and return.
  cancelTokenSources.set(cancelTokenId, cancelTokenSource);

  return [cancelTokenId, cancelTokenSource.token];
}

export function findPanelCancelToken(cancelTokenId: PanelCancelTokenId) {
  const cancelTokenSource = cancelTokenSources.get(cancelTokenId);

  // If no cancel token was found, we'll assume it was cancelled!
  if (!cancelTokenSource) {
    return cancelledCancelToken;
  }

  return cancelTokenSource.token;
}

export function triggerPanelCancelToken(cancelTokenId: PanelCancelTokenId) {
  // Find the CancelTokenSource.
  const cancelTokenSource = cancelTokenSources.get(cancelTokenId);
  if (!cancelTokenSource) {
    return;
  }

  // If one was found, remove it from the map and trigger it.
  cancelTokenSources.delete(cancelTokenId);
  cancelTokenSource.cancel();
}
